import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { eventTypeColor } from "components/scrapEyeDataDashboard/VideoPlayer/utils";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useEffect } from "react";
import { useDisableHotkeyScopes } from "../hooks/useDisableHotkeyScopes";
import { usePauseVideoWhileOpen } from "../hooks/usePauseVideoWhileOpen";
import { EventType } from "src/store/api/generatedApi";
import { ScrapEyeLayoutMap } from "components/scrapEyeLayout/ScrapEyeLayoutMap";
import { useHotkeyToAreaMapping } from "../hooks/useAreaHotkeys";
import { useBinNumberEntry } from "../hooks/useBinNumberEntry";
import { useWaitForKeyRelease } from "../hooks/useWaitForKeyRelease";
import { VideoSnapshotsPreview } from "components/common/VideoSnapshotsPreview";
import { AREA_MODAL_DISABLED_SCOPES } from "components/manualReview/hotkeyScopes";

type AreaSelectionModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSelectArea: (area: string) => void;
  eventType: EventType;
  areaColors?: Record<string, string>;
  areaMaterials?: Record<string, string>;
  disableScopes?: readonly string[];
  openedOnKeyRelease?: boolean;
  onEditMaterials?: () => void;
  location?: string;
}>;

export const AreaSelectionModal = ({
  open,
  onClose,
  onSelectArea,
  eventType,
  areaColors,
  areaMaterials,
  disableScopes = AREA_MODAL_DISABLED_SCOPES,
  openedOnKeyRelease = false,
  onEditMaterials,
  location,
}: AreaSelectionModalProps) => {
  const { t } = useTenantTranslation();
  const hotkeyToArea = useHotkeyToAreaMapping(location);

  useDisableHotkeyScopes(disableScopes, open);
  usePauseVideoWhileOpen(open);

  const keyboardActiveRef = useWaitForKeyRelease(open, {
    keysAlreadyReleased: openedOnKeyRelease,
  });

  const handleSelectArea = useCallback(
    (area: string) => {
      onSelectArea(area);
      onClose();
    },
    [onSelectArea, onClose]
  );

  const {
    binEntryActive,
    binBuffer,
    binEntryAvailable,
    handleKeyDown: handleBinNumberKeyDown,
  } = useBinNumberEntry({
    location,
    enabled: open,
    onSelectBin: handleSelectArea,
  });

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!keyboardActiveRef.current) {
        return;
      }

      // Ignore if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (handleBinNumberKeyDown(event)) {
        return;
      }

      const key = event.key.toLowerCase();
      const area = hotkeyToArea[key];

      if (area) {
        event.preventDefault();
        event.stopPropagation();
        handleSelectArea(area);
      }
    };

    // Use capture phase to intercept before other handlers (including react-hotkeys-hook)
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    open,
    hotkeyToArea,
    handleSelectArea,
    keyboardActiveRef,
    handleBinNumberKeyDown,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: eventTypeColor(eventType),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "white",
                fontWeight: "bold",
              }}
            >
              {eventType.toUpperCase()}
            </Typography>
          </Box>
          <span>
            {eventType === "lift" ? t("selectLiftArea") : t("selectDropArea")}
          </span>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          {onEditMaterials != null && (
            <Button
              variant="text"
              size="small"
              startIcon={<EditIcon />}
              onClick={onEditMaterials}
            >
              {t("editMaterials")}
            </Button>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <VideoSnapshotsPreview compact />

          <Typography variant="body2" color="text.secondary">
            {t("clickAreaOrPressHotkey")}
          </Typography>

          {binEntryAvailable ? (
            binEntryActive ? (
              <Typography variant="body2" color="primary" fontWeight="bold">
                {t("binNumberEntrySelecting", { buffer: binBuffer })}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("binNumberEntryHint")}
              </Typography>
            )
          ) : null}

          <ScrapEyeLayoutMap
            onSelectArea={handleSelectArea}
            areaColors={areaColors}
            areaMaterials={areaMaterials}
            location={location}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
